if (!customElements.get('pf-best-loop')) {
  customElements.define('pf-best-loop', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('.pf-bc-track');
      this.cards = [...this.track.children];
      this.cloneNumber = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay === 'false';
      this.pauseButton = this.querySelector('[data-pause]');
      this.querySelector('.pf-bc-controls').hidden = this.cards.length < 2;
      this.update = () => {
        this.pauseButton.textContent = this.paused ? '▶' : 'Ⅱ';
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.setAttribute('aria-label', this.paused ? 'Resume carousel' : 'Pause carousel');
      };
      const move = direction => {
        this.paused = true; this.update();
        this.track.scrollLeft += direction * this.step;
      };
      this.querySelector('[data-prev]').addEventListener('click', () => move(-1), options);
      this.querySelector('[data-next]').addEventListener('click', () => move(1), options);
      this.pauseButton.addEventListener('click', () => { this.paused = !this.paused; this.update(); }, options);
      this.track.addEventListener('pointerenter', e => { this.hovered = e.pointerType !== 'touch'; }, options);
      this.track.addEventListener('pointerleave', () => { this.hovered = false; }, options);
      this.track.addEventListener('pointerdown', () => { this.dragging = true; }, options);
      window.addEventListener('pointerup', () => { this.dragging = false; }, options);
      window.addEventListener('pointercancel', () => { this.dragging = false; }, options);
      this.track.addEventListener('keydown', e => {
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); move(e.key === 'ArrowRight' ? 1 : -1); }
      }, options);
      this.track.addEventListener('scroll', () => {
        this.position = this.track.scrollLeft;
        if (this.loop && !this.contains(document.activeElement) && !this.dragging) {
          if (this.track.scrollLeft >= this.loop * 2) this.track.scrollLeft -= this.loop;
          if (this.track.scrollLeft < this.loop) this.track.scrollLeft += this.loop;
        }
        const index = Math.round(this.track.scrollLeft / this.step) % this.cards.length;
        this.querySelector('.pf-bc-count').textContent = `${index + 1} / ${this.cards.length}`;
      }, options);
      document.addEventListener('shopify:block:select', e => {
        const card = this.cards.find(c => c.dataset.blockId === e.detail.blockId);
        if (card) { this.paused = true; this.update(); this.track.scrollLeft = card.offsetLeft - this.track.offsetLeft; }
      }, options);
      this.resize = new ResizeObserver(() => this.build());
      this.resize.observe(this.track);
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
      this.observer.observe(this);
      this.motion.addEventListener('change', () => this.build(), options);
      this.update();
      const tick = time => {
        const elapsed = Math.min(time - (this.last || time), 40); this.last = time;
        if (this.loop && this.visible && !document.hidden && !this.paused && !this.motion.matches && !this.hovered && !this.dragging && !this.contains(document.activeElement)) {
          this.position = (this.position ?? this.track.scrollLeft) + elapsed * this.speed / 1000;
          this.track.scrollLeft = this.position;
          if (this.position >= this.loop * 2) { this.position -= this.loop; this.track.scrollLeft = this.position; }
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    build() {
      this.track.querySelectorAll('[data-clone]').forEach(c => c.remove());
      this.step = this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap);
      const logoGroup = this.closest('.pf-editorial')?.querySelector('.pf-brand-group');
      const logoWidth = logoGroup?.getBoundingClientRect().width || Math.max(Number(this.dataset.brandCount) * 240, window.innerWidth);
      this.speed = logoWidth / (Number(this.dataset.duration) || 22);
      this.loop = 0;
      if (this.motion.matches || this.cards.length < 2) return;
      this.loop = this.step * this.cards.length;
      const clone = card => {
        const copy = card.cloneNode(true);
        copy.dataset.clone = 'true'; copy.removeAttribute('data-block-id'); copy.removeAttribute('data-shopify-editor-block');
        copy.setAttribute('aria-hidden', 'true');
        const suffix = `-loop-${++this.cloneNumber}`;
        const ids = new Map();
        copy.querySelectorAll('[id]').forEach(el => { ids.set(el.id, el.id + suffix); el.id += suffix; });
        copy.querySelectorAll('[aria-labelledby],[aria-describedby],[for]').forEach(el => {
          ['aria-labelledby', 'aria-describedby', 'for'].forEach(attr => {
            if (el.hasAttribute(attr)) el.setAttribute(attr, el.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
          });
        });
        copy.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
        copy.querySelectorAll('a,button,[tabindex]').forEach(a => a.tabIndex = -1);
        return copy;
      };
      this.track.prepend(...this.cards.map(clone));
      for (let i = 0; i < Math.ceil(this.track.clientWidth / this.loop) + 1; i++) this.track.append(...this.cards.map(clone));
      this.track.scrollLeft = this.loop;
      this.position = this.loop;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame); this.resize?.disconnect(); this.observer?.disconnect(); this.controller?.abort(); this.controller = null;
      this.track?.querySelectorAll('[data-clone]').forEach(c => c.remove());
    }
  });
}
